import React, { useCallback, useMemo, useRef, useState } from 'react';
import { NativeModules, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, G, Line, Path, Rect, Text as SvgText } from './chartSvg';
import * as Sharing from './chartSharing';
import { captureRef, releaseCapture } from '@/constants/utils/lazyCapture';
import PropTypes from 'prop-types';
import Icon from '@/components/Icon';
import { chartSpecToTable, formatChartValue } from '@/constants/utils/chart_spec';
import { chartPointSegments, chartLinePath, chartAreaPath } from '@/lib/chartGeometry';

const WIDTH = 760;
const HEIGHT = 350;
const MARGIN = { top: 18, right: 16, bottom: 52, left: 68 };
const PLOT_WIDTH = WIDTH - MARGIN.left - MARGIN.right;
const PLOT_HEIGHT = HEIGHT - MARGIN.top - MARGIN.bottom;
const DEFAULT_COLORS = ['#4c9b5f', '#5b8def', '#d9903d', '#a56de2', '#d85c75', '#46a6a6'];
const PIE_CENTER = { x: WIDTH / 2, y: 142 };
const PIE_OUTER_RADIUS = 112;
const PIE_INNER_RADIUS = 58;
const RADAR_CENTER = { x: WIDTH / 2, y: HEIGHT / 2 };
const RADAR_RADIUS = 125;
const AXIS_TICK_FONT_SIZE = 16;
const AXIS_LABEL_FONT_SIZE = 15;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const polarPoint = (centerX, centerY, radius, angle) => {
  const radians = (angle * Math.PI) / 180;
  return {
    x: centerX + radius * Math.cos(radians),
    y: centerY + radius * Math.sin(radians),
  };
};
const donutSlicePath = (startAngle, endAngle, outerRadius = PIE_OUTER_RADIUS) => {
  const outerStart = polarPoint(PIE_CENTER.x, PIE_CENTER.y, outerRadius, startAngle);
  const outerEnd = polarPoint(PIE_CENTER.x, PIE_CENTER.y, outerRadius, endAngle);
  const innerEnd = polarPoint(PIE_CENTER.x, PIE_CENTER.y, PIE_INNER_RADIUS, endAngle);
  const innerStart = polarPoint(PIE_CENTER.x, PIE_CENTER.y, PIE_INNER_RADIUS, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${PIE_INNER_RADIUS} ${PIE_INNER_RADIUS} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ');
};
const escapeXml = value => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const waitForPaint = () => new Promise(resolve => {
  if (Platform.OS !== 'web' || typeof requestAnimationFrame !== 'function') {
    setTimeout(resolve, 50);
    return;
  }
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function InlineChart({ spec, colors }) {
  const captureTargetRef = useRef(null);
  const svgRef = useRef(null);
  const [viewMode, setViewMode] = useState('chart');
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredPointIndex, setHoverIndex] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const chart = useMemo(() => {
    const values = spec.series.flatMap(series => series.data.map(point => point.y)).filter(Number.isFinite);
    let min = Math.min(0, ...values);
    let max = Math.max(0, ...values);
    if (spec.type === 'stacked-bar') {
      const totals = spec.series[0].data.map((_, pointIndex) => spec.series.reduce((sum, series) => {
        const value = series.data[pointIndex].y;
        if (Number.isFinite(value)) sum[value < 0 ? 0 : 1] += value;
        return sum;
      }, [0, 0]));
      min = Math.min(0, ...totals.map(total => total[0]));
      max = Math.max(0, ...totals.map(total => total[1]));
    }
    if (spec.type === 'radar') min = 0;
    if (min === max) max = min + 1;

    const rawRange = max - min;
    if (spec.type === 'line' && min !== 0) min -= rawRange * 0.08;
    if (spec.type === 'line' && max !== 0) max += rawRange * 0.08;
    const range = max - min || 1;

    const labels = spec.series[0].data.map(point => point.x);
    const xStep = labels.length > 1 ? PLOT_WIDTH / (labels.length - 1) : PLOT_WIDTH;
    const lineX = index => MARGIN.left + (labels.length > 1 ? index * xStep : PLOT_WIDTH / 2);
    const numericXValues = spec.series.flatMap(series => series.data.filter(point => Number.isFinite(point.y)).map(point => Number(point.x)));
    const scatterMinX = Math.min(...numericXValues);
    const scatterMaxX = Math.max(...numericXValues);
    const scatterRangeX = scatterMaxX - scatterMinX || 1;
    const scatterX = value => MARGIN.left + ((Number(value) - scatterMinX) / scatterRangeX) * PLOT_WIDTH;
    const y = value => MARGIN.top + PLOT_HEIGHT - ((value - min) / range) * PLOT_HEIGHT;
    const ticks = Array.from({ length: 5 }, (_, index) => min + (range * index) / 4);

    return { labels, lineX, scatterX, scatterMinX, scatterMaxX, y, ticks, min, max };
  }, [spec]);
  const hoverIndex = Number.isInteger(hoveredPointIndex) && hoveredPointIndex >= 0 && hoveredPointIndex < chart.labels.length
    ? hoveredPointIndex : null;
  const displayNote = [spec.series.some(series => series.data.some(point => point.y === null)) ? 'Gaps indicate missing data.' : '', spec.note].filter(Boolean).join(' ');
  const table = useMemo(() => chartSpecToTable(spec), [spec]);
  const pieSegments = useMemo(() => {
    if (spec.type !== 'pie') return [];
    const points = spec.series[0].data;
    const total = points.reduce((sum, point) => sum + point.y, 0);
    let cursor = -90;
    return points.map((point, index) => {
      const startAngle = cursor;
      const endAngle = index === points.length - 1
        ? 270
        : cursor + (point.y / total) * 360;
      cursor = endAngle;
      return {
        ...point,
        startAngle,
        endAngle,
        percentage: (point.y / total) * 100,
        color: point.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
      };
    });
  }, [spec]);

  const textColor = colors.text_color_primary || '#e6e6e6';
  const secondaryText = colors.text_color_secondary || '#a6a6a6';
  const gridColor = colors.border_color_input || '#2a2a2a';
  const backgroundColor = colors.solid_color_primary || 'transparent';
  const labelEvery = Math.max(1, Math.ceil(chart.labels.length / 12));
  const barSlotWidth = PLOT_WIDTH / chart.labels.length;
  const barGroupWidth = barSlotWidth * 0.72;
  const barWidth = Math.max(1, barGroupWidth / spec.series.length);
  const zeroY = chart.y(clamp(0, chart.min, chart.max));
  const usesCartesianAxes = !['pie', 'heatmap', 'radar', 'horizontal-bar'].includes(spec.type);
  const usesCartesianHoverMarkers = !['heatmap', 'radar', 'horizontal-bar'].includes(spec.type);
  const xAxisLabels = spec.type === 'scatter'
    ? Array.from({ length: 5 }, (_, index) => {
      const value = chart.scatterMinX + ((chart.scatterMaxX - chart.scatterMinX) * index) / 4;
      return { label: formatChartValue(value, { yFormat: 'number' }), x: chart.scatterX(value) };
    })
    : chart.labels.map((label, index) => ({
      label,
      x: ['bar', 'stacked-bar', 'histogram', 'combo'].includes(spec.type)
        ? MARGIN.left + barSlotWidth * index + barSlotWidth / 2
        : chart.lineX(index),
    }));
  const hoverX = hoverIndex === null || spec.type === 'pie'
    ? null
    : (spec.type === 'scatter'
      ? chart.scatterX(spec.series[0].data[hoverIndex].x)
      : ['bar', 'stacked-bar', 'histogram', 'heatmap', 'combo'].includes(spec.type)
      ? MARGIN.left + barSlotWidth * hoverIndex + barSlotWidth / 2
      : chart.lineX(hoverIndex));

  const handleMouseMove = useCallback((event) => {
    if (Platform.OS !== 'web') return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const clientX = event.nativeEvent?.clientX ?? event.clientX;
    const clientY = event.nativeEvent?.clientY ?? event.clientY;
    if (!rect || !Number.isFinite(clientX) || !Number.isFinite(clientY) || rect.width <= 0 || rect.height <= 0) return;

    const svgX = ((clientX - rect.left) / rect.width) * WIDTH;
    const svgY = ((clientY - rect.top) / rect.height) * HEIGHT;
    if (spec.type === 'pie') {
      const dx = svgX - PIE_CENTER.x;
      const dy = svgY - PIE_CENTER.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < PIE_INNER_RADIUS || distance > PIE_OUTER_RADIUS + 8) {
        setHoverIndex(null);
        return;
      }
      const angle = ((Math.atan2(dy, dx) * 180) / Math.PI + 450) % 360 - 90;
      const index = pieSegments.findIndex(segment => angle >= segment.startAngle && angle < segment.endAngle);
      setHoverIndex(index >= 0 ? index : pieSegments.length - 1);
      return;
    }

    if (spec.type === 'horizontal-bar') {
      const rowHeight = PLOT_HEIGHT / chart.labels.length;
      const index = Math.floor((svgY - MARGIN.top) / rowHeight);
      setHoverIndex(svgY >= MARGIN.top && svgY <= MARGIN.top + PLOT_HEIGHT
        ? clamp(index, 0, chart.labels.length - 1)
        : null);
      return;
    }

    if (spec.type === 'radar') {
      const dx = svgX - RADAR_CENTER.x;
      const dy = svgY - RADAR_CENTER.y;
      if (Math.sqrt(dx * dx + dy * dy) > RADAR_RADIUS + 35) {
        setHoverIndex(null);
        return;
      }
      const angle = (Math.atan2(dy, dx) + Math.PI / 2 + Math.PI * 2) % (Math.PI * 2);
      setHoverIndex(Math.round(angle / (Math.PI * 2 / chart.labels.length)) % chart.labels.length);
      return;
    }

    if (svgX < MARGIN.left || svgX > WIDTH - MARGIN.right) {
      setHoverIndex(null);
      return;
    }

    if (spec.type === 'scatter') {
      const index = spec.series[0].data.reduce((closest, point, pointIndex, points) => (
        Math.abs(chart.scatterX(point.x) - svgX) < Math.abs(chart.scatterX(points[closest].x) - svgX)
          ? pointIndex
          : closest
      ), 0);
      setHoverIndex(index);
      return;
    }

    const index = ['bar', 'stacked-bar', 'histogram', 'heatmap', 'combo'].includes(spec.type)
      ? Math.floor((svgX - MARGIN.left) / barSlotWidth)
      : Math.round((svgX - MARGIN.left) / (chart.labels.length > 1 ? PLOT_WIDTH / (chart.labels.length - 1) : PLOT_WIDTH));
    setHoverIndex(clamp(index, 0, chart.labels.length - 1));
  }, [barSlotWidth, chart, pieSegments, spec]);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;
    setMenuOpen(false);
    setIsDownloading(true);
    setDownloadError('');
    setHoverIndex(null);

    try {
      if (viewMode !== 'chart' || hoverIndex !== null) {
        setViewMode('chart');
        await waitForPaint();
      }

      const safeName = (spec.title || 'chart')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 60) || 'chart';

      if (Platform.OS === 'web') {
        const sourceSvg = captureTargetRef.current?.querySelector?.('svg');
        if (!sourceSvg) throw new Error('Chart is not ready to download');

        const titleHeight = (spec.title ? 32 : 0) + (spec.subtitle ? 28 : 0);
        const noteHeight = displayNote ? 34 : 12;
        const exportHeight = HEIGHT + titleHeight + noteHeight;
        const clonedSvg = sourceSvg.cloneNode(true);
        clonedSvg.setAttribute('x', '0');
        clonedSvg.setAttribute('y', String(titleHeight));
        clonedSvg.setAttribute('width', String(WIDTH));
        clonedSvg.setAttribute('height', String(HEIGHT));

        const exportSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${exportHeight}" viewBox="0 0 ${WIDTH} ${exportHeight}">
          <rect width="100%" height="100%" fill="${escapeXml(backgroundColor)}"/>
          ${spec.title ? `<text x="16" y="24" fill="${escapeXml(textColor)}" font-family="system-ui,-apple-system,sans-serif" font-size="17" font-weight="700">${escapeXml(spec.title)}</text>` : ''}
          ${spec.subtitle ? `<text x="16" y="${spec.title ? 50 : 24}" fill="${escapeXml(secondaryText)}" font-family="system-ui,-apple-system,sans-serif" font-size="14">${escapeXml(spec.subtitle.slice(0, 105))}</text>` : ''}
          ${clonedSvg.outerHTML}
          ${displayNote ? `<text x="16" y="${exportHeight - 12}" fill="${escapeXml(secondaryText)}" font-family="system-ui,-apple-system,sans-serif" font-size="12">${escapeXml(displayNote.slice(0, 120))}</text>` : ''}
        </svg>`;

        const svgBlob = new Blob([exportSvg], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);
        try {
          const image = new Image();
          image.src = svgUrl;
          await new Promise((resolve, reject) => {
            image.onload = resolve;
            image.onerror = reject;
          });
          const canvas = document.createElement('canvas');
          canvas.width = WIDTH * 2;
          canvas.height = exportHeight * 2;
          const context = canvas.getContext('2d');
          context.scale(2, 2);
          context.drawImage(image, 0, 0, WIDTH, exportHeight);
          const pngBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
          if (!pngBlob) throw new Error('Could not create chart image');
          const filename = `${safeName}.png`;
          const file = typeof File === 'function' ? new File([pngBlob], filename, { type: 'image/png' }) : null;
          const isMobileBrowser = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')
            || ((navigator.maxTouchPoints || 0) > 1 && window.innerWidth < 768);
          if (isMobileBrowser && file && typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
            try {
              await navigator.share({ files: [file], title: spec.title || 'Chart' });
            } catch (shareError) {
              if (shareError?.name !== 'AbortError') downloadBlob(pngBlob, filename);
            }
          } else {
            downloadBlob(pngBlob, filename);
          }
        } finally {
          URL.revokeObjectURL(svgUrl);
        }
      } else if (Platform.OS === 'windows') {
        const drawing = svgRef.current?.getSvgString();
        if (!drawing || !NativeModules.WindowsFiles?.saveSvg) throw new Error('Chart saving is unavailable. Rebuild the Windows application.');
        const headingHeight = (spec.title ? 32 : 0) + (spec.subtitle ? 28 : 0);
        const legendHeight = spec.series.length > 1 ? 28 : 0;
        const exportHeight = HEIGHT + headingHeight + legendHeight + (displayNote ? 34 : 12);
        const nested = drawing.replace('<svg ', `<svg x="0" y="${headingHeight + legendHeight}" width="${WIDTH}" height="${HEIGHT}" `);
        const exported = `<svg xmlns="http://www.w3.org/2000/svg" width="${WIDTH}" height="${exportHeight}" viewBox="0 0 ${WIDTH} ${exportHeight}">
          <rect width="100%" height="100%" fill="${escapeXml(backgroundColor)}"/>
          ${spec.title ? `<text x="16" y="24" fill="${escapeXml(textColor)}" font-size="17">${escapeXml(spec.title)}</text>` : ''}
          ${spec.subtitle ? `<text x="16" y="${spec.title ? 50 : 24}" fill="${escapeXml(secondaryText)}" font-size="14">${escapeXml(spec.subtitle.slice(0, 105))}</text>` : ''}
          ${legendHeight ? spec.series.map((series, index) => `<text x="${16 + index * WIDTH / spec.series.length}" y="${headingHeight + 20}" fill="${escapeXml(series.color || DEFAULT_COLORS[index])}" font-size="12">${escapeXml(series.name)}</text>`).join('') : ''}
          ${nested}
          ${displayNote ? `<text x="16" y="${exportHeight - 12}" fill="${escapeXml(secondaryText)}" font-size="12">${escapeXml(displayNote.slice(0, 120))}</text>` : ''}
        </svg>`;
        await NativeModules.WindowsFiles.saveSvg(exported, `${safeName}.svg`);
      } else {
        const uri = await captureRef(captureTargetRef.current, {
          format: 'png',
          quality: 1,
          result: 'tmpfile',
        });
        try {
          if (!(await Sharing.isAvailableAsync())) throw new Error('Saving is not available');
          await Sharing.shareAsync(uri, {
            mimeType: 'image/png',
            dialogTitle: `Save ${spec.title || 'chart'}`,
            suggestedFilename: `${safeName}.png`,
            UTI: 'public.png',
          });
        } finally { if (Platform.OS === 'macos') await releaseCapture(uri); }
      }
    } catch (error) {
      console.error('[InlineChart] Download failed:', error);
      setDownloadError(error?.message || 'Could not save the chart image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }, [backgroundColor, displayNote, hoverIndex, isDownloading, secondaryText, spec, textColor, viewMode]);

  return (
    <View
      style={[styles.container, { backgroundColor }]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Chart options"
        onPress={() => setMenuOpen(open => !open)}
        style={({ pressed, hovered }) => [
          styles.menuTrigger,
          {
            backgroundColor: hovered || menuOpen ? gridColor : (colors.solid_color_secondary || '#292929'),
            opacity: pressed ? 0.75 : 1,
          },
        ]}
      >
        <Icon name="ellipsis" size={16} color={textColor} />
      </Pressable>

      {menuOpen && (
        <View style={[styles.menu, { backgroundColor: colors.solid_color_secondary || '#333', borderColor: gridColor }]}>
          <Pressable
            accessibilityRole="button"
            onPress={() => {
              setViewMode(mode => mode === 'chart' ? 'table' : 'chart');
              setMenuOpen(false);
              setHoverIndex(null);
            }}
            style={({ pressed, hovered }) => [styles.menuItem, (pressed || hovered) && { backgroundColor: gridColor }]}
          >
            <Icon name={viewMode === 'chart' ? 'table' : 'chart-line'} size={15} color={textColor} />
            <Text style={[styles.menuItemText, { color: textColor }]}>
              {viewMode === 'chart' ? 'View as table' : 'View as chart'}
            </Text>
          </Pressable>
          <Pressable
            accessibilityRole="button"
            disabled={isDownloading}
            onPress={handleDownload}
            style={({ pressed, hovered }) => [styles.menuItem, (pressed || hovered) && { backgroundColor: gridColor }]}
          >
            <Icon name="download" size={15} color={textColor} />
            <Text style={[styles.menuItemText, { color: textColor }]}>
              {isDownloading ? 'Preparing image…' : Platform.OS === 'windows' ? 'Save SVG image' : 'Download as image'}
            </Text>
          </Pressable>
        </View>
      )}

      <View
        ref={captureTargetRef}
        collapsable={false}
        accessibilityRole={viewMode === 'chart' ? 'image' : undefined}
        accessibilityLabel={`${spec.title || 'Chart'}. ${spec.subtitle || ''}`}
        style={[styles.captureTarget, { backgroundColor }]}
      >
        {!!spec.title && <Text style={[styles.title, { color: textColor }]}>{spec.title}</Text>}
        {!!spec.subtitle && <Text style={[styles.subtitle, { color: secondaryText }]}>{spec.subtitle}</Text>}

      {viewMode === 'table' ? (
        <ScrollView horizontal showsHorizontalScrollIndicator>
          <View style={[styles.table, { borderColor: gridColor }]}>
            <View style={[styles.tableRow, styles.tableHeader, { borderColor: gridColor }]}>
              {table.headers.map((header, index) => (
                <Text key={`${header}-${index}`} style={[styles.tableCell, styles.tableHeaderText, { color: textColor, borderColor: gridColor }]}>
                  {header}
                </Text>
              ))}
            </View>
            {table.rows.map((row, rowIndex) => (
              <View key={`row-${rowIndex}`} style={[styles.tableRow, { borderColor: gridColor }]}>
                {row.map((cell, cellIndex) => (
                  <Text key={`${cellIndex}-${cell}`} style={[styles.tableCell, { color: cellIndex === 0 ? textColor : secondaryText, borderColor: gridColor }]}>
                    {cell}
                  </Text>
                ))}
              </View>
            ))}
          </View>
        </ScrollView>
      ) : (
        <>
        {spec.series.length > 1 && (
        <View style={styles.legend}>
          {spec.series.map((series, index) => (
            <View key={`${series.name}-${index}`} style={styles.legendItem}>
              <View style={[styles.legendSwatch, { backgroundColor: series.color || DEFAULT_COLORS[index] }]} />
              <Text style={[styles.legendText, { color: secondaryText }]} numberOfLines={1}>{series.name}</Text>
            </View>
          ))}
        </View>
      )}

      <View
        style={styles.svgWrapper}
        onMouseMove={Platform.OS === 'web' ? handleMouseMove : undefined}
        onMouseLeave={Platform.OS === 'web' ? () => setHoverIndex(null) : undefined}
      >
        <Svg ref={svgRef} width="100%" height="100%" viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
          {usesCartesianAxes && (
            <>
          {chart.ticks.map((tick, index) => {
            const tickY = chart.y(tick);
            return (
              <G key={`y-${index}`}>
                <Line
                  x1={MARGIN.left}
                  x2={WIDTH - MARGIN.right}
                  y1={tickY}
                  y2={tickY}
                  stroke={gridColor}
                  strokeWidth={1}
                  strokeDasharray="3 4"
                />
                <SvgText
                  x={MARGIN.left - 10}
                  y={tickY + 4}
                  fill={secondaryText}
                  fontSize={AXIS_TICK_FONT_SIZE}
                  textAnchor="end"
                >
                  {formatChartValue(tick, spec)}
                </SvgText>
              </G>
            );
          })}

          {xAxisLabels.map(({ label, x }, index) => {
            if (index % labelEvery !== 0 && index !== xAxisLabels.length - 1) return null;
            return (
              <SvgText
                key={`x-${index}`}
                x={x}
                y={HEIGHT - 26}
                fill={secondaryText}
                fontSize={AXIS_TICK_FONT_SIZE}
                textAnchor="middle"
              >
                {label.length > 12 ? `${label.slice(0, 11)}…` : label}
              </SvgText>
            );
          })}
            </>
          )}

          {spec.type === 'pie' ? (
            <G>
              {pieSegments.map((segment, index) => (
                <Path
                  key={`${segment.x}-${index}`}
                  d={donutSlicePath(
                    segment.startAngle,
                    segment.endAngle,
                    hoverIndex === index ? PIE_OUTER_RADIUS + 7 : PIE_OUTER_RADIUS,
                  )}
                  fill={segment.color}
                  stroke={backgroundColor}
                  strokeWidth={3}
                />
              ))}
              {hoverIndex !== null && pieSegments[hoverIndex] && (
                <G pointerEvents="none">
                  <SvgText
                    x={PIE_CENTER.x}
                    y={PIE_CENTER.y - 4}
                    fill={textColor}
                    fontSize={18}
                    fontWeight="700"
                    textAnchor="middle"
                  >
                    {pieSegments[hoverIndex].x}
                  </SvgText>
                  <SvgText
                    x={PIE_CENTER.x}
                    y={PIE_CENTER.y + 18}
                    fill={secondaryText}
                    fontSize={15}
                    textAnchor="middle"
                  >
                    {`${formatChartValue(pieSegments[hoverIndex].y, spec, false)} (${pieSegments[hoverIndex].percentage.toFixed(1)}%)`}
                  </SvgText>
                </G>
              )}
              {pieSegments.map((segment, index) => {
                const columns = Math.min(6, pieSegments.length);
                const columnWidth = 112;
                const row = Math.floor(index / columns);
                const column = index % columns;
                const rowCount = Math.ceil(pieSegments.length / columns);
                const itemsInRow = row === rowCount - 1 && pieSegments.length % columns
                  ? pieSegments.length % columns
                  : columns;
                const startX = (WIDTH - itemsInRow * columnWidth) / 2;
                const legendY = 286 + row * 25;
                return (
                  <G key={`legend-${segment.x}-${index}`}>
                    <Circle cx={startX + column * columnWidth} cy={legendY} r={5} fill={segment.color} />
                    <SvgText
                      x={startX + column * columnWidth + 11}
                      y={legendY + 4}
                      fill={textColor}
                      fontSize={15}
                    >
                      {segment.x.length > 11 ? `${segment.x.slice(0, 10)}…` : segment.x}
                    </SvgText>
                  </G>
                );
              })}
            </G>
          ) : spec.type === 'heatmap' ? (
            <G>
              {spec.series.map((series, seriesIndex) => {
                const cellHeight = PLOT_HEIGHT / spec.series.length;
                return (
                  <G key={`${series.name}-${seriesIndex}`}>
                    <SvgText
                      x={MARGIN.left - 10}
                      y={MARGIN.top + seriesIndex * cellHeight + cellHeight / 2 + 5}
                      fill={secondaryText}
                      fontSize={14}
                      textAnchor="end"
                    >
                      {series.name.length > 13 ? `${series.name.slice(0, 12)}…` : series.name}
                    </SvgText>
                    {series.data.map((point, pointIndex) => Number.isFinite(point.y) ? (
                      <Rect
                        key={`${point.x}-${pointIndex}`}
                        x={MARGIN.left + pointIndex * barSlotWidth + 1}
                        y={MARGIN.top + seriesIndex * cellHeight + 1}
                        width={Math.max(1, barSlotWidth - 2)}
                        height={Math.max(1, cellHeight - 2)}
                        rx={3}
                        fill={series.color || DEFAULT_COLORS[0]}
                        opacity={0.15 + ((point.y - chart.min) / (chart.max - chart.min)) * 0.85}
                      />
                    ) : null)}
                  </G>
                );
              })}
              {chart.labels.map((label, index) => (
                <SvgText
                  key={`heat-label-${label}-${index}`}
                  x={MARGIN.left + index * barSlotWidth + barSlotWidth / 2}
                  y={MARGIN.top + PLOT_HEIGHT + 24}
                  fill={secondaryText}
                  fontSize={14}
                  textAnchor="middle"
                >
                  {label.length > 10 ? `${label.slice(0, 9)}…` : label}
                </SvgText>
              ))}
            </G>
          ) : spec.type === 'radar' ? (
            <G>
              {[0.25, 0.5, 0.75, 1].map(level => {
                const points = chart.labels.map((_, index) => (
                  polarPoint(RADAR_CENTER.x, RADAR_CENTER.y, RADAR_RADIUS * level, -90 + index * (360 / chart.labels.length))
                ));
                return (
                  <Path
                    key={`radar-grid-${level}`}
                    d={`${points.map((point, index) => `${index ? 'L' : 'M'} ${point.x} ${point.y}`).join(' ')} Z`}
                    fill="none"
                    stroke={gridColor}
                    strokeWidth={1}
                  />
                );
              })}
              {chart.labels.map((label, index) => {
                const edge = polarPoint(RADAR_CENTER.x, RADAR_CENTER.y, RADAR_RADIUS, -90 + index * (360 / chart.labels.length));
                const labelPoint = polarPoint(RADAR_CENTER.x, RADAR_CENTER.y, RADAR_RADIUS + 22, -90 + index * (360 / chart.labels.length));
                return (
                  <G key={`radar-axis-${label}-${index}`}>
                    <Line x1={RADAR_CENTER.x} y1={RADAR_CENTER.y} x2={edge.x} y2={edge.y} stroke={gridColor} />
                    <SvgText x={labelPoint.x} y={labelPoint.y + 5} fill={secondaryText} fontSize={14} textAnchor="middle">
                      {label.length > 12 ? `${label.slice(0, 11)}…` : label}
                    </SvgText>
                  </G>
                );
              })}
              {spec.series.map((series, seriesIndex) => {
                const segments = chartPointSegments(series.data, (point, pointIndex) => (
                  polarPoint(
                    RADAR_CENTER.x,
                    RADAR_CENTER.y,
                    RADAR_RADIUS * (Math.max(0, point.y) / chart.max),
                    -90 + pointIndex * (360 / chart.labels.length),
                  )
                ));
                const complete = series.data.every(point => Number.isFinite(point.y));
                // Radar axes form a circle; join the two edge runs only when
                // both boundary measurements exist, leaving internal gaps.
                if (!complete && segments.length > 1 && Number.isFinite(series.data[0].y) && Number.isFinite(series.data.at(-1).y)) {
                  segments[0] = [...segments.pop(), ...segments[0]];
                }
                const points = segments.flat();
                const seriesColor = series.color || DEFAULT_COLORS[seriesIndex];
                return (
                  <G key={`${series.name}-${seriesIndex}`}>
                    <Path
                      d={`${chartLinePath(segments)}${complete ? ' Z' : ''}`}
                      fill={complete ? seriesColor : 'none'}
                      fillOpacity={0.2}
                      stroke={seriesColor}
                      strokeWidth={2.5}
                    />
                    {points.map((point, pointIndex) => (
                      <Circle key={`radar-point-${pointIndex}`} cx={point.x} cy={point.y} r={3.5} fill={seriesColor} />
                    ))}
                  </G>
                );
              })}
            </G>
          ) : spec.type === 'horizontal-bar' ? (
            <G>
              {chart.labels.map((label, pointIndex) => {
                const rowHeight = PLOT_HEIGHT / chart.labels.length;
                const groupHeight = rowHeight * 0.7;
                const itemHeight = groupHeight / spec.series.length;
                return (
                  <G key={`horizontal-${label}-${pointIndex}`}>
                    <SvgText
                      x={MARGIN.left - 10}
                      y={MARGIN.top + pointIndex * rowHeight + rowHeight / 2 + 5}
                      fill={secondaryText}
                      fontSize={14}
                      textAnchor="end"
                    >
                      {label.length > 13 ? `${label.slice(0, 12)}…` : label}
                    </SvgText>
                    {spec.series.map((series, seriesIndex) => Number.isFinite(series.data[pointIndex].y) ? (
                      <Rect
                        key={`${series.name}-${seriesIndex}`}
                        x={MARGIN.left + ((Math.min(0, series.data[pointIndex].y) - chart.min) / (chart.max - chart.min)) * PLOT_WIDTH}
                        y={MARGIN.top + pointIndex * rowHeight + (rowHeight - groupHeight) / 2 + seriesIndex * itemHeight}
                        width={(Math.abs(series.data[pointIndex].y) / (chart.max - chart.min)) * PLOT_WIDTH}
                        height={Math.max(2, itemHeight - 2)}
                        rx={2}
                        fill={series.color || DEFAULT_COLORS[seriesIndex]}
                      />
                    ) : null)}
                  </G>
                );
              })}
            </G>
          ) : spec.type === 'stacked-bar' ? (
            <G>
              {chart.labels.map((label, pointIndex) => {
                const runningTotals = [0, 0];
                return spec.series.map((series, seriesIndex) => {
                  const value = series.data[pointIndex].y;
                  if (!Number.isFinite(value)) return null;
                  const side = value < 0 ? 0 : 1;
                  const top = chart.y(runningTotals[side] + value);
                  const bottom = chart.y(runningTotals[side]);
                  runningTotals[side] += value;
                  return (
                    <Rect
                      key={`${label}-${series.name}-${seriesIndex}`}
                      x={MARGIN.left + barSlotWidth * pointIndex + barSlotWidth * 0.14}
                      y={Math.min(top, bottom)}
                      width={barSlotWidth * 0.72}
                      height={Math.abs(bottom - top)}
                      rx={seriesIndex === spec.series.length - 1 ? 2 : 0}
                      fill={series.color || DEFAULT_COLORS[seriesIndex]}
                    />
                  );
                });
              })}
            </G>
          ) : spec.type === 'histogram' ? (
            <G>
              {spec.series[0].data.map((point, pointIndex) => {
                if (!Number.isFinite(point.y)) return null;
                const pointY = chart.y(point.y);
                return (
                  <Rect
                    key={`${point.x}-${pointIndex}`}
                    x={MARGIN.left + barSlotWidth * pointIndex}
                    y={Math.min(pointY, zeroY)}
                    width={Math.max(1, barSlotWidth - 1)}
                    height={Math.max(1, Math.abs(zeroY - pointY))}
                    fill={spec.series[0].color || DEFAULT_COLORS[0]}
                  />
                );
              })}
            </G>
          ) : spec.type === 'scatter' ? (
            <G>
              {spec.series.map((series, seriesIndex) => (
                <G key={`${series.name}-${seriesIndex}`}>
                  {series.data.map((point, pointIndex) => Number.isFinite(point.y) ? (
                    <Circle
                      key={`${point.x}-${pointIndex}`}
                      cx={chart.scatterX(point.x)}
                      cy={chart.y(point.y)}
                      r={5}
                      fill={series.color || DEFAULT_COLORS[seriesIndex]}
                      opacity={hoverIndex === null || hoverIndex === pointIndex ? 1 : 0.55}
                    />
                  ) : null)}
                </G>
              ))}
            </G>
          ) : spec.type === 'combo' ? (
            <G>
              {spec.series.map((series, seriesIndex) => {
                const seriesColor = series.color || DEFAULT_COLORS[seriesIndex];
                if (series.chartType === 'bar') {
                  const barSeries = spec.series.filter(item => item.chartType === 'bar');
                  const barIndex = barSeries.indexOf(series);
                  const comboBarWidth = barGroupWidth / barSeries.length;
                  return (
                    <G key={`${series.name}-${seriesIndex}`}>
                      {series.data.map((point, pointIndex) => {
                        if (!Number.isFinite(point.y)) return null;
                        const pointY = chart.y(point.y);
                        return (
                          <Rect
                            key={`${point.x}-${pointIndex}`}
                            x={MARGIN.left + barSlotWidth * pointIndex + (barSlotWidth - barGroupWidth) / 2 + barIndex * comboBarWidth}
                            y={Math.min(pointY, zeroY)}
                            width={Math.max(1, comboBarWidth - 2)}
                            height={Math.max(1, Math.abs(zeroY - pointY))}
                            rx={2}
                            fill={seriesColor}
                          />
                        );
                      })}
                    </G>
                  );
                }
                const path = chartLinePath(chartPointSegments(series.data, (point, pointIndex) => ({
                  x: MARGIN.left + barSlotWidth * pointIndex + barSlotWidth / 2, y: chart.y(point.y),
                })));
                return <G key={`${series.name}-${seriesIndex}`}>
                  <Path d={path} fill="none" stroke={seriesColor} strokeWidth={3} />
                  {series.data.length <= 24 && series.data.map((point, pointIndex) => Number.isFinite(point.y) ?
                    <Circle key={pointIndex} cx={MARGIN.left + barSlotWidth * pointIndex + barSlotWidth / 2}
                      cy={chart.y(point.y)} r={3} fill={seriesColor} /> : null)}
                </G>;
              })}
            </G>
          ) : spec.type === 'bar' ? spec.series.map((series, seriesIndex) => (
            <G key={`${series.name}-${seriesIndex}`}>
              {series.data.map((point, pointIndex) => {
                if (!Number.isFinite(point.y)) return null;
                const pointY = chart.y(point.y);
                return (
                  <Rect
                    key={`${point.x}-${pointIndex}`}
                    x={MARGIN.left + barSlotWidth * pointIndex + (barSlotWidth - barGroupWidth) / 2 + seriesIndex * barWidth}
                    y={Math.min(pointY, zeroY)}
                    width={Math.max(1, barWidth - 2)}
                    height={Math.max(1, Math.abs(zeroY - pointY))}
                    rx={2}
                    fill={series.color || DEFAULT_COLORS[seriesIndex]}
                  />
                );
              })}
            </G>
          )) : spec.series.map((series, seriesIndex) => {
            const segments = chartPointSegments(series.data, (point, pointIndex) => ({ x: chart.lineX(pointIndex), y: chart.y(point.y) }));
            const path = chartLinePath(segments);
            const seriesColor = series.color || DEFAULT_COLORS[seriesIndex];
            const areaPath = spec.type === 'area'
              ? chartAreaPath(segments, zeroY)
              : null;
            return (
              <G key={`${series.name}-${seriesIndex}`}>
                {!!areaPath && <Path d={areaPath} fill={seriesColor} fillOpacity={0.18} stroke="none" />}
                <Path d={path} fill="none" stroke={seriesColor} strokeWidth={2.5} />
                {series.data.length <= 24 && series.data.map((point, pointIndex) => Number.isFinite(point.y) ? (
                  <Circle
                    key={`${point.x}-${pointIndex}`}
                    cx={chart.lineX(pointIndex)}
                    cy={chart.y(point.y)}
                    r={3}
                    fill={seriesColor}
                  />
                ) : null)}
              </G>
            );
          })}

          {usesCartesianAxes && !!spec.yLabel && (
            <SvgText
              x={15}
              y={MARGIN.top + PLOT_HEIGHT / 2}
              fill={secondaryText}
              fontSize={AXIS_LABEL_FONT_SIZE}
              textAnchor="middle"
              transform={`rotate(-90 15 ${MARGIN.top + PLOT_HEIGHT / 2})`}
            >
              {spec.yLabel}
            </SvgText>
          )}
          {usesCartesianAxes && !!spec.xLabel && (
            <SvgText x={MARGIN.left + PLOT_WIDTH / 2} y={HEIGHT - 4} fill={secondaryText} fontSize={AXIS_LABEL_FONT_SIZE} textAnchor="middle">
              {spec.xLabel}
            </SvgText>
          )}

          {spec.type !== 'pie' && hoverIndex !== null && hoverX !== null && (
            <G pointerEvents="none">
              {usesCartesianHoverMarkers && (
                <>
                  <Line
                    x1={hoverX}
                    x2={hoverX}
                    y1={MARGIN.top}
                    y2={MARGIN.top + PLOT_HEIGHT}
                    stroke={secondaryText}
                    strokeWidth={1}
                    strokeDasharray="3 3"
                    opacity={0.7}
                  />
                  {spec.series.map((series, seriesIndex) => Number.isFinite(series.data[hoverIndex].y) ? (
                    <Circle
                      key={`hover-${seriesIndex}`}
                      cx={hoverX}
                      cy={chart.y(series.data[hoverIndex].y)}
                      r={5}
                      fill={series.color || DEFAULT_COLORS[seriesIndex]}
                      stroke={backgroundColor}
                      strokeWidth={2}
                    />
                  ) : null)}
                </>
              )}
              <Rect
                x={clamp(hoverX + 12, MARGIN.left, WIDTH - MARGIN.right - 190)}
                y={MARGIN.top + 8}
                width={190}
                height={30 + spec.series.length * 19}
                rx={7}
                fill={colors.solid_color_secondary || '#292929'}
                stroke={gridColor}
              />
              <SvgText
                x={clamp(hoverX + 24, MARGIN.left + 12, WIDTH - MARGIN.right - 178)}
                y={MARGIN.top + 27}
                fill={textColor}
                fontSize={16}
                fontWeight="700"
              >
                {chart.labels[hoverIndex]}
              </SvgText>
              {spec.series.map((series, seriesIndex) => (
                <G key={`tooltip-${seriesIndex}`}>
                  <Circle
                    cx={clamp(hoverX + 24, MARGIN.left + 12, WIDTH - MARGIN.right - 178)}
                    cy={MARGIN.top + 44 + seriesIndex * 19}
                    r={4}
                    fill={series.color || DEFAULT_COLORS[seriesIndex]}
                  />
                  <SvgText
                    x={clamp(hoverX + 34, MARGIN.left + 22, WIDTH - MARGIN.right - 168)}
                    y={MARGIN.top + 48 + seriesIndex * 19}
                    fill={secondaryText}
                    fontSize={14}
                  >
                    {`${series.name}: ${formatChartValue(series.data[hoverIndex].y, spec, false)}`}
                  </SvgText>
                </G>
              ))}
            </G>
          )}
        </Svg>
      </View>
      </>
      )}

      {!!displayNote && <Text style={[styles.note, { color: secondaryText }]}>{displayNote}</Text>}
      </View>
      {!!downloadError && <Text accessibilityRole="alert" style={{ color: colors.color_destructive || textColor, padding: 12 }}>{downloadError}</Text>}
    </View>
  );
}

InlineChart.propTypes = {
  spec: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    alignSelf: 'stretch',
    flexShrink: 1,
    borderRadius: 10,
    paddingTop: 12,
    marginVertical: 8,
    position: 'relative',
    overflow: 'visible',
  },
  captureTarget: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    flexShrink: 1,
  },
  menuTrigger: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 12,
  },
  menu: {
    position: 'absolute',
    top: 44,
    right: 2,
    minWidth: 205,
    padding: 5,
    borderRadius: 12,
    borderWidth: 1,
    zIndex: 11,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    }),
  },
  menuItem: {
    minHeight: 40,
    paddingHorizontal: 11,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  menuItemText: {
    fontSize: 14,
    fontWeight: '500',
  },
  title: {
    fontSize: 17,
    fontWeight: '700',
    paddingHorizontal: 4,
    paddingRight: 46,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
    marginTop: 4,
    paddingHorizontal: 4,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 10,
    paddingHorizontal: 4,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: 180,
  },
  legendSwatch: {
    width: 9,
    height: 9,
    borderRadius: 5,
    marginRight: 5,
  },
  legendText: {
    fontSize: 14,
  },
  svgWrapper: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    flexShrink: 1,
    aspectRatio: WIDTH / HEIGHT,
    maxHeight: 420,
    marginTop: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'crosshair',
    }),
  },
  table: {
    minWidth: 520,
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 14,
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
  },
  tableHeader: {
    backgroundColor: 'rgba(127,127,127,0.12)',
  },
  tableCell: {
    width: 150,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 11,
    borderRightWidth: 1,
    fontSize: 14,
  },
  tableHeaderText: {
    fontWeight: '700',
  },
  note: {
    fontSize: 14,
    lineHeight: 20,
    paddingHorizontal: 4,
    paddingBottom: 8,
  },
});

export default React.memo(InlineChart);
